// FILE: client/src/pages/secure/ExclusiveListingAgreementPage.js
// (This is a NEW file)

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';

const ExclusiveListingAgreementPage = () => {
    const [clients, setClients] = useState([]);
    const [formData, setFormData] = useState({
        client_id: '',
        property_address: '',
        tax_id: '',
        listing_price: '',
        lease_rate: '',
        term_start_date: new Date().toISOString().split('T')[0],
        term_end_date: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [generatedFile, setGeneratedFile] = useState(null);
    const apiClient = useApiClient();

    useEffect(() => {
        // Fetch clients to populate the seller dropdown
        apiClient.get('/api/clients').then(response => {
            if (Array.isArray(response)) {
                setClients(response);
            } else {
                setClients([]);
                console.error("API response for clients was not an array:", response);
            }
        }).catch(err => {
            console.error("Failed to fetch clients", err);
            setClients([]);
        });
    }, [apiClient]);

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setGeneratedFile(null);
        try {
            const response = await apiClient.post('/api/forms/generate-ela', formData, { responseType: 'blob' });
            
            const file = new Blob([response.data], { type: 'application/pdf' });
            const fileURL = URL.createObjectURL(file);
            setGeneratedFile(fileURL);

        } catch (error)            {
            console.error("Failed to generate agreement", error);
            alert(error.response?.data?.message || 'An error occurred while generating the PDF.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="p-4 md:p-8">
            <Link to="/dashboard/forms" className="text-indigo-600 hover:underline mb-6 inline-block">&larr; Back to Forms Portal</Link>
            <div className="max-w-4xl mx-auto bg-white p-8 rounded-lg shadow-lg">
                <h1 className="text-2xl font-bold text-slate-800 mb-2">Exclusive Listing Agreement (Sale/Lease)</h1>
                <p className="text-slate-500 mb-6">Fill out the property and term details below to generate the agreement.</p>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <fieldset className="border p-4 rounded-md">
                        <legend className="text-lg font-semibold px-2">Parties & Property</legend>
                        <div className="space-y-4 pt-2">
                             <div>
                                <label htmlFor="client_id" className="block text-sm font-medium text-gray-700">Select Seller (Owner)</label>
                                <select id="client_id" name="client_id" value={formData.client_id} onChange={handleChange} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm">
                                    <option value="">-- Choose a client --</option>
                                    {clients.map(client => (
                                        <option key={client.client_id} value={client.client_id}>{client.first_name} {client.last_name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="property_address" className="block text-sm font-medium text-gray-700">Property Common Address</label>
                                <input type="text" id="property_address" name="property_address" value={formData.property_address} onChange={handleChange} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm" />
                            </div>
                            <div>
                                <label htmlFor="tax_id" className="block text-sm font-medium text-gray-700">Tax Identification Number(s)</label>
                                <input type="text" id="tax_id" name="tax_id" value={formData.tax_id} onChange={handleChange} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm" />
                            </div>
                        </div>
                    </fieldset>

                    <fieldset className="border p-4 rounded-md">
                        <legend className="text-lg font-semibold px-2">Listing Terms</legend>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                            <div>
                                <label htmlFor="listing_price" className="block text-sm font-medium text-gray-700">Asking Sale Price ($)</label>
                                <input type="number" step="0.01" id="listing_price" name="listing_price" value={formData.listing_price} onChange={handleChange} placeholder="e.g., 500000" className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm" />
                            </div>
                             <div>
                                <label htmlFor="lease_rate" className="block text-sm font-medium text-gray-700">Asking Lease Rate ($ / sq. ft.)</label>
                                <input type="number" step="0.01" id="lease_rate" name="lease_rate" value={formData.lease_rate} onChange={handleChange} placeholder="e.g., 25.50" className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm" />
                            </div>
                            <div>
                                <label htmlFor="term_start_date" className="block text-sm font-medium text-gray-700">Term Start Date</label>
                                <input type="date" id="term_start_date" name="term_start_date" value={formData.term_start_date} onChange={handleChange} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm" />
                            </div>
                            <div>
                                <label htmlFor="term_end_date" className="block text-sm font-medium text-gray-700">Term End Date</label>
                                <input type="date" id="term_end_date" name="term_end_date" value={formData.term_end_date} onChange={handleChange} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm" />
                            </div>
                        </div>
                    </fieldset>
                    
                    <div className="flex justify-end">
                        <button type="submit" disabled={isSubmitting} className="px-6 py-2 bg-slate-800 text-white rounded-md disabled:bg-indigo-400">
                            {isSubmitting ? 'Generating PDF...' : 'Generate Listing Agreement'}
                        </button>
                    </div>
                </form>

                {generatedFile && (
                    <div className="mt-8 border-t pt-6">
                        <h3 className="text-lg font-bold">Agreement Generated Successfully</h3>
                        <p className="mb-4">Review the generated PDF below. The next step will be to send it for e-signature.</p>
                        <a href={generatedFile} download={`ExclusiveListingAgreement_${formData.property_address.replace(/ /g, '_')}.pdf`} className="inline-block px-6 py-2 bg-green-600 text-white rounded-md">
                            Download PDF
                        </a>
                        <div className="mt-4 h-96">
                           <iframe src={generatedFile} width="100%" height="100%" title="Generated Agreement Preview"></iframe>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExclusiveListingAgreementPage;
